import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { apply, applyWhen, schema, validate } from '@angular/forms/signals';
import { CustomValidators } from '@shared-ikp/utils/validators';
import { validateOptionalWithControlValidator, validateWithControlValidator } from '@shared-ikp/utils/signal-forms.util';

export enum VertragspartnerMode {
  VERWALTETER_KUNDE = 'VERWALTETER_KUNDE',
  ACCOUNT_KUNDE = 'ACCOUNT_KUNDE',
  BEARBEITBAR = 'BEARBEITBAR',
}
export interface AdresseFormValue {
  plz: string;
  ort: string;
  strasse: string;
  hausnummer: string;
  stiege: string;
  tuernummer: string;
}

export interface RechnungsempfaengerFormValue {
  hasAbweichendenRechnungsempfaenger: boolean;
  name: string;
  kundennummer: string;
  adresse: AdresseFormValue;
  uid: string;
  firmenbuchnummer: string;
}

export type VertragspartnerTyp = 'privat' | 'unternehmen' | null;

export interface PrivatpersonKontaktdatenFormValue {
  vorname: string;
  nachname: string;
  email: string;
  telefon: string;
  geburtsdatum: NgbDateStruct | null;
}

export interface UnternehmenKontaktdatenFormValue {
  name: string;
  geburtsdatum: NgbDateStruct | null;
  uid: string;
  firmenbuchnummer: string;
  email: string;
  telefon: string;
}

export interface PrivatUnternehmenFormValue {
  vertragspartnerTyp: VertragspartnerTyp;
  privatpersonKontaktdaten: PrivatpersonKontaktdatenFormValue | null;
  unternehmenKontaktdaten: UnternehmenKontaktdatenFormValue | null;
  rechnungsempfaenger: RechnungsempfaengerFormValue;
}

export interface UnterschriftFormValue {
  unterschrift: string | null;
  hasZustimmung: boolean;
}

export const adresseSchema = schema<AdresseFormValue>(path => {
  validate(path.plz, validateOptionalWithControlValidator(CustomValidators.plzValidator, 'address'));
  validate(path.ort, validateOptionalWithControlValidator(CustomValidators.cityValidator, 'address'));
  validate(path.strasse, validateOptionalWithControlValidator(CustomValidators.streetValidator, 'address'));
  validate(path.hausnummer, validateOptionalWithControlValidator(CustomValidators.houseNumberStaircaseValidator, 'address'));
  validate(path.stiege, validateOptionalWithControlValidator(CustomValidators.houseNumberStaircaseValidator, 'address'));
  validate(path.tuernummer, validateWithControlValidator(CustomValidators.doorNumberValidator, 'address'));
});

export const privatpersonKontaktdatenSchema = schema<PrivatpersonKontaktdatenFormValue>(path => {
  validate(path.vorname, validateOptionalWithControlValidator(CustomValidators.firstNameValidator, 'contact'));
  validate(path.nachname, validateOptionalWithControlValidator(CustomValidators.lastNameValidator, 'contact'));
  validate(path.email, validateWithControlValidator(CustomValidators.optionalEmailValidator, 'contact'));
  validate(path.telefon, validateWithControlValidator(CustomValidators.optionalPhoneNumberValidator, 'contact'));
});

export const privatpersonGeburtsdatumSchema = schema<PrivatpersonKontaktdatenFormValue>(path => {
  validate(path.geburtsdatum, validateWithControlValidator(CustomValidators.optionalBirthdateValidator, 'contact'));
});

export const unternehmenskennungenSchema = schema<{ uid: string; firmenbuchnummer: string }>(path => {
  validate(path.uid, validateWithControlValidator(CustomValidators.optionalUidValidator, 'contact'));
  validate(path.firmenbuchnummer, validateWithControlValidator(CustomValidators.optionalFirmenbuchNrValidator, 'contact'));
});

export const unternehmenKontaktdatenSchema = schema<UnternehmenKontaktdatenFormValue>(path => {
  validate(path.name, validateOptionalWithControlValidator(CustomValidators.companyNameValidator, 'contact'));
  validate(path.geburtsdatum, validateWithControlValidator(CustomValidators.optionalBirthdateValidator, 'contact'));
  apply(path, unternehmenskennungenSchema);
  validate(path.email, validateWithControlValidator(CustomValidators.optionalEmailValidator, 'contact'));
  validate(path.telefon, validateWithControlValidator(CustomValidators.optionalPhoneNumberValidator, 'contact'));
});

export const privatUnternehmenKontaktdatenSchema = schema<PrivatUnternehmenFormValue>(path => {
  applyWhen(
    path.privatpersonKontaktdaten,
    ctx => ctx.valueOf(path.vertragspartnerTyp) === 'privat' && ctx.value() !== null,
    privatpersonKontaktdatenSchema,
  );
  applyWhen(
    path.unternehmenKontaktdaten,
    ctx => ctx.valueOf(path.vertragspartnerTyp) === 'unternehmen' && ctx.value() !== null,
    unternehmenKontaktdatenSchema,
  );
});

const rechnungsempfaengerAdresseSchema = schema<RechnungsempfaengerFormValue>(path => {
  validate(path.name, validateOptionalWithControlValidator(CustomValidators.companyNameValidator, 'rechnungsempfaenger'));
  apply(path.adresse, adresseSchema);
});

export const abweichenderRechnungsempfaengerSchema = schema<RechnungsempfaengerFormValue>(path => {
  applyWhen(path, ctx => ctx.value().hasAbweichendenRechnungsempfaenger, rechnungsempfaengerAdresseSchema);
});
